import React, { useState, useEffect } from 'react';
import { X, RefreshCw, AlertTriangle, CheckCircle, Loader2, FileText } from 'lucide-react';
import { libraryApi } from '../api/client';
import type { LibraryComponent, Component } from '../schemas/validation';
import { ComponentEditModal } from './ComponentEditModal';

interface LibraryComponentEditorProps {
  isOpen: boolean;
  onClose: () => void;
  libraryComponent: LibraryComponent | null;
  onSaveSuccess?: () => void;
  onSyncSuccess?: () => void;
}

interface UsageInfo {
  totalPages: number;
  totalInstances: number;
  pages: Array<{
    pageId: string;
    pageTitle: string;
    componentCount: number;
  }>;
}

export const LibraryComponentEditor: React.FC<LibraryComponentEditorProps> = ({
  isOpen,
  onClose,
  libraryComponent,
  onSaveSuccess,
  onSyncSuccess,
}) => {
  const [componentData, setComponentData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isLoadingUsage, setIsLoadingUsage] = useState(false);
  const [usage, setUsage] = useState<UsageInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [syncResult, setSyncResult] = useState<{
    updatedPages: Array<{ pageId: string; pageTitle: string; componentsUpdated: number }>;
    totalComponentsUpdated: number;
  } | null>(null);

  // Load component data and usage when modal opens
  useEffect(() => {
    if (isOpen && libraryComponent) {
      loadComponentData();
      loadUsageInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, libraryComponent]);

  const loadComponentData = async () => {
    if (!libraryComponent) return;

    try {
      setIsLoading(true);
      setError(null);
      const response = await libraryApi.getComponent(libraryComponent.id);
      setComponentData(response.component);
    } catch (err) {
      console.error('Failed to load component data:', err);
      setError('Failed to load component data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadUsageInfo = async () => {
    if (!libraryComponent) return;

    try {
      setIsLoadingUsage(true);
      const response = await libraryApi.getComponentUsage(libraryComponent.id);
      setUsage(response.usage);
    } catch (err) {
      console.error('Failed to load usage info:', err);
      // Don't show error for usage - it's not critical
    } finally {
      setIsLoadingUsage(false);
    }
  };

  const handleSave = async (updatedData: any) => {
    if (!libraryComponent) return;

    try {
      setIsSaving(true);
      setError(null);
      setSuccessMessage(null);

      await libraryApi.updateComponentContent(libraryComponent.id, updatedData);

      setSuccessMessage('Library component saved successfully');
      setComponentData({ ...componentData, data: updatedData });
      setShowEditModal(false);

      // Reload usage info in case it changed
      loadUsageInfo();

      if (onSaveSuccess) {
        onSaveSuccess();
      }
    } catch (err) {
      console.error('Failed to save component:', err);
      setError('Failed to save component');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSync = async () => {
    if (!libraryComponent) return;

    try {
      setIsSyncing(true);
      setError(null);
      setSuccessMessage(null);
      setSyncResult(null);

      const response = await libraryApi.syncComponent(libraryComponent.id);

      setSyncResult({
        updatedPages: response.updatedPages,
        totalComponentsUpdated: response.totalComponentsUpdated,
      });

      if (response.updatedPages.length > 0) {
        setSuccessMessage(`Successfully synced to ${response.updatedPages.length} page(s)`);
        // Notify parent to refresh page components
        if (onSyncSuccess) {
          onSyncSuccess();
        }
      } else {
        setSuccessMessage('No pages needed updating');
      }
    } catch (err) {
      console.error('Failed to sync component:', err);
      setError('Failed to sync component to pages');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleClose = () => {
    setComponentData(null);
    setUsage(null);
    setError(null);
    setSuccessMessage(null);
    setSyncResult(null);
    setShowEditModal(false);
    onClose();
  };

  if (!isOpen || !libraryComponent) return null;

  // Create a mock component for the edit modal
  // We use 'as any' because LibraryComponent.type may include legacy types
  // that are no longer in the Component union, but ComponentEditModal handles them gracefully
  const mockComponent: Component | null = componentData
    ? ({
        id: libraryComponent.id,
        type: libraryComponent.type,
        order: 0,
        visible: true,
        data: componentData.data,
        createdAt: new Date(libraryComponent.createdAt).toISOString(),
        updatedAt: new Date(libraryComponent.updatedAt).toISOString(),
      } as any)
    : null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[70]">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col border border-gray-100">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-100">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Edit Library Component</h2>
              <p className="text-sm text-gray-500 mt-1">{libraryComponent.name}</p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {isLoading ? (
              <div className="text-center py-12">
                <Loader2 size={32} className="animate-spin mx-auto text-primary-600 mb-4" />
                <p className="text-gray-600">Loading component...</p>
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
                <AlertTriangle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-800 font-medium">Error</p>
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Success Message */}
                {successMessage && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start space-x-3">
                    <CheckCircle size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-green-800 font-medium">Success</p>
                      <p className="text-green-600 text-sm">{successMessage}</p>
                    </div>
                  </div>
                )}

                {/* Component Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-3">Component Details</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Type:</span>
                      <span className="ml-2 text-gray-900 capitalize">{libraryComponent.type}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Visibility:</span>
                      <span className="ml-2 text-gray-900">
                        {libraryComponent.isPublic ? 'Public' : 'Private'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Created:</span>
                      <span className="ml-2 text-gray-900">
                        {new Date(libraryComponent.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Updated:</span>
                      <span className="ml-2 text-gray-900">
                        {new Date(libraryComponent.updatedAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  {libraryComponent.description && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <span className="text-gray-500 text-sm">Description:</span>
                      <p className="text-gray-900 text-sm mt-1">{libraryComponent.description}</p>
                    </div>
                  )}
                </div>

                {/* Usage Info */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-3">Usage Information</h3>
                  {isLoadingUsage ? (
                    <div className="flex items-center space-x-2 text-gray-600">
                      <Loader2 size={16} className="animate-spin" />
                      <span className="text-sm">Loading usage info...</span>
                    </div>
                  ) : usage ? (
                    <div className="space-y-2">
                      <p className="text-sm text-gray-700">
                        This component is used in <strong>{usage.totalPages}</strong> page(s) (
                        {usage.totalInstances} total instance(s))
                      </p>
                      {usage.pages.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-gray-500 mb-2">Pages using this component:</p>
                          <div className="max-h-32 overflow-y-auto space-y-1">
                            {usage.pages.map(page => (
                              <div
                                key={page.pageId}
                                className="flex items-center justify-between text-xs bg-white rounded px-2 py-1"
                              >
                                <span className="text-gray-700">{page.pageTitle}</span>
                                <span className="text-gray-500">
                                  {page.componentCount} instance(s)
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-600">
                      This component is not currently used in any pages.
                    </p>
                  )}
                </div>

                {/* Sync Result */}
                {syncResult && syncResult.updatedPages.length > 0 && (
                  <div className="bg-green-50 rounded-lg p-4">
                    <h3 className="font-medium text-gray-900 mb-3">Sync Result</h3>
                    <p className="text-sm text-gray-700 mb-2">
                      Updated {syncResult.totalComponentsUpdated} component(s) across{' '}
                      {syncResult.updatedPages.length} page(s):
                    </p>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {syncResult.updatedPages.map(page => (
                        <div
                          key={page.pageId}
                          className="flex items-center justify-between text-xs bg-white rounded px-2 py-1"
                        >
                          <span className="text-gray-700">{page.pageTitle}</span>
                          <span className="text-green-600">{page.componentsUpdated} updated</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => setShowEditModal(true)}
                    disabled={!componentData || isSaving || isSyncing}
                    className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FileText size={18} />
                    <span>Edit Component Content</span>
                  </button>

                  <button
                    onClick={handleSync}
                    disabled={!usage || usage.totalPages === 0 || isSaving || isSyncing}
                    className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSyncing ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        <span>Syncing...</span>
                      </>
                    ) : (
                      <>
                        <RefreshCw size={18} />
                        <span>Sync to {usage?.totalPages || 0} Page(s)</span>
                      </>
                    )}
                  </button>
                </div>

                {/* Help Text */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="text-amber-800 font-medium">How syncing works</p>
                      <p className="text-amber-700 mt-1">
                        When you sync, all pages that have this library component will be updated
                        with the latest content. This only affects components that were originally
                        added from this library item.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-100 px-6 py-4">
            <div className="flex items-center justify-end">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-md transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Component Edit Modal */}
      {showEditModal && mockComponent && (
        <ComponentEditModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          component={mockComponent}
          onSave={(_componentId, updatedData) => {
            if (updatedData) {
              handleSave(updatedData);
            }
          }}
          isLibraryEdit={true}
        />
      )}
    </>
  );
};
